import { useEffect, useRef } from "react";

/**
 * Canvas-based animated starfield used for the "Ask AI" hover + open-panel ambient.
 *
 * Props:
 *  - active: boolean → fade in/out
 *  - mode: "ambient" (slow drift, sparse) | "burst" (radial burst from button on hover)
 *  - origin: { x, y } in viewport pixels (used for burst mode)
 *  - color: hex string (default brand cyan)
 *  - density: 0.1..2 multiplier on particle count
 *  - className: extra classes
 *  - zIndex: z-index for the canvas
 */
export default function AIStarfield({
  active,
  mode = "ambient",
  origin = null,
  color = "#41B5E6",
  density = 1,
  className = "",
  zIndex = 60,
}) {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const particlesRef = useRef([]);
  const stateRef = useRef({ opacity: 0, active: false, mode, origin });
  const lastSpawnRef = useRef(0);

  // Keep state ref synced so the animation loop sees the latest props without re-running
  useEffect(() => {
    stateRef.current.active = active;
    stateRef.current.mode = mode;
    stateRef.current.origin = origin;
  }, [active, mode, origin]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + "px";
      canvas.style.height = window.innerHeight + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const hex = color.replace("#", "");
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const colorRGB = `${r},${g},${b}`;

    const spawnAmbient = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const isMobile = w < 640;
      return {
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.18,
        vy: -(0.12 + Math.random() * 0.35),
        size: 0.5 + Math.random() * (isMobile ? 1.5 : 2.2),
        life: 0,
        ttl: 4000 + Math.random() * 4500,
        twinkle: Math.random() * Math.PI * 2,
        twinkleSpeed: 0.04 + Math.random() * 0.05,
        kind: Math.random() > 0.7 ? "star" : "dot",
        hue: Math.random(),
      };
    };

    const spawnBurst = (ox, oy) => {
      const angle = Math.random() * Math.PI * 2;
      const speed = 0.5 + Math.random() * 2.4;
      return {
        x: ox + (Math.random() - 0.5) * 18,
        y: oy + (Math.random() - 0.5) * 18,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 0.4,
        size: 0.6 + Math.random() * 2.6,
        life: 0,
        ttl: 900 + Math.random() * 1100,
        twinkle: Math.random() * Math.PI * 2,
        twinkleSpeed: 0.08 + Math.random() * 0.06,
        kind: Math.random() > 0.55 ? "star" : "dot",
        hue: Math.random(),
      };
    };

    const drawStar = (cx, cy, size, alpha) => {
      // Cross + plus glyph — classic AI sparkle
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.translate(cx, cy);
      // outer glow
      const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, size * 4);
      grad.addColorStop(0, `rgba(${colorRGB},${0.85 * alpha})`);
      grad.addColorStop(0.4, `rgba(${colorRGB},${0.18 * alpha})`);
      grad.addColorStop(1, `rgba(${colorRGB},0)`);
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(0, 0, size * 4, 0, Math.PI * 2);
      ctx.fill();
      // crisp 4-point star
      ctx.fillStyle = `rgba(255,255,255,${alpha})`;
      ctx.beginPath();
      ctx.moveTo(0, -size * 1.6);
      ctx.quadraticCurveTo(0, 0, size * 1.6, 0);
      ctx.quadraticCurveTo(0, 0, 0, size * 1.6);
      ctx.quadraticCurveTo(0, 0, -size * 1.6, 0);
      ctx.quadraticCurveTo(0, 0, 0, -size * 1.6);
      ctx.fill();
      ctx.restore();
    };

    const drawDot = (cx, cy, size, alpha) => {
      ctx.save();
      ctx.globalAlpha = alpha;
      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, size * 3);
      grad.addColorStop(0, `rgba(${colorRGB},${0.7 * alpha})`);
      grad.addColorStop(1, `rgba(${colorRGB},0)`);
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(cx, cy, size * 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = `rgba(255,255,255,${alpha * 0.9})`;
      ctx.beginPath();
      ctx.arc(cx, cy, Math.max(0.6, size * 0.7), 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    };

    let last = performance.now();
    const loop = (now) => {
      const dt = Math.min(50, now - last);
      last = now;
      const w = window.innerWidth;
      const h = window.innerHeight;

      // Fade master opacity
      const target = stateRef.current.active ? 1 : 0;
      stateRef.current.opacity += (target - stateRef.current.opacity) * 0.08;

      // Stop drawing entirely once fully invisible to save GPU
      if (stateRef.current.opacity < 0.005 && !stateRef.current.active) {
        ctx.clearRect(0, 0, w, h);
        particlesRef.current = [];
        rafRef.current = requestAnimationFrame(loop);
        return;
      }

      // Spawn while active
      if (stateRef.current.active) {
        const mode = stateRef.current.mode;
        if (mode === "ambient") {
          const desired = Math.floor(38 * density);
          while (particlesRef.current.length < desired) {
            particlesRef.current.push(spawnAmbient());
          }
        } else if (mode === "burst") {
          // ~14 particles per ~16ms while hover active
          if (now - lastSpawnRef.current > 22 && particlesRef.current.length < 220 * density) {
            const o = stateRef.current.origin || { x: w - 120, y: 50 };
            for (let i = 0; i < Math.ceil(2 * density); i++) {
              particlesRef.current.push(spawnBurst(o.x, o.y));
            }
            lastSpawnRef.current = now;
          }
        }
      }

      ctx.clearRect(0, 0, w, h);

      const alive = [];
      for (const p of particlesRef.current) {
        p.life += dt;
        if (p.life >= p.ttl) continue;
        p.x += p.vx * (dt * 0.06);
        p.y += p.vy * (dt * 0.06);
        p.twinkle += p.twinkleSpeed;
        if (p.x < -20 || p.x > w + 20 || p.y < -20 || p.y > h + 20) continue;

        // Alpha: fade in for first 15%, hold, fade out for last 30%; with twinkle modulation
        const t = p.life / p.ttl;
        let a;
        if (t < 0.15) a = t / 0.15;
        else if (t > 0.7) a = (1 - t) / 0.3;
        else a = 1;
        const twk = 0.55 + Math.abs(Math.sin(p.twinkle)) * 0.45;
        const alpha = Math.max(0, a * twk * stateRef.current.opacity);
        if (p.kind === "star") drawStar(p.x, p.y, p.size, alpha);
        else drawDot(p.x, p.y, p.size, alpha);

        alive.push(p);
      }
      particlesRef.current = alive;

      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
    };
    // intentionally only setup once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [color, density]);

  return (
    <canvas
      ref={canvasRef}
      className={`pointer-events-none fixed inset-0 ${className}`}
      style={{ zIndex }}
      data-testid="ai-starfield"
      aria-hidden="true"
    />
  );
}
